/**
 * Shared ApiClient instance for the web app.
 *
 * Token source: the HS256 session JWT from the passkey login flow
 * (features/auth/session.ts). Signed-link surfaces (MyDossier via reminder
 * email) work without a session — getToken returns null and the by-link
 * routes authenticate with the token in the path instead.
 */

import { ApiClient } from "@re-platform/api-client";

import { getAccessToken, refreshSession } from "../features/auth/session";

// Default to the page origin: the vite dev server proxies /document,
// /mortgage, etc. to the per-service local ports (see vite.config.ts).
// Cloud builds set VITE_API_BASE_URL to the HTTP API gateway explicitly.
const baseUrl =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  window.location.origin;

export const api = new ApiClient({
  baseUrl,
  // Renews proactively when the token is close to expiry, so a long task (a
  // multi-file dossier upload) doesn't die at the 15-minute mark holding a
  // token it acquired at the start.
  getToken: getAccessToken,
  // The reactive half: a token can still be rejected mid-flight — a laptop that
  // slept, clock skew, or a session revoked server-side. Both paths funnel into
  // the same single-flight refresh, so N concurrent 401s make ONE refresh call
  // rather than N racing ones that revoke each other's rotated token.
  onUnauthorized: async () => (await refreshSession()) !== null,
  timeoutMs: 30_000,
});
